import { zodResolver } from '@hookform/resolvers/zod';
import { parseISO } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectDoctor, selectFunc } from 'redux/features/auth/authSlice';
import {
  buscarConveniosAgenda,
  buscarPacientesPorNome,
  buscarProcedimentosAgenda,
  criarAgenda,
  editarAgenda,
} from 'services/agenda';
import useSWR from 'swr';
import { medicos, resolveUsuarioAgenda } from 'utils/agenda';
import { getApiErrorMessage } from 'utils/apiErrors';
import { formatDateISO } from 'utils/helpers';
import { z } from 'zod';

const LIMITE_NOME_AGENDA = 50;

const agendaSchema = z.object({
  pacienteBusca: z.string().trim().min(3, 'Digite pelo menos 3 caracteres.'),
  paciente: z
    .object({
      CODPAC: z.union([z.string(), z.number()]),
      NOMEPAC: z.string().optional(),
    })
    .nullable()
    .refine(Boolean, { message: 'Selecione um paciente da lista.' }),
  procedimento: z.string().min(1, 'Selecione o procedimento.'),
  convenio: z.string().min(1, 'Selecione o convênio.'),
  horario: z
    .string()
    .trim()
    .regex(/^\d{3,4}$/, 'Informe a hora no formato INT (ex: 930 ou 0930).'),
  dataHora: z.date().refine((d) => !Number.isNaN(d.getTime()), {
    message: 'Informe a data e hora.',
  }),
  observacao: z.string().max(250, 'Observação deve ter no máximo 250 caracteres.').optional(),
  atividade: z
    .string()
    .trim()
    .min(1, 'Informe a atividade.')
    .max(15, 'Atividade deve ter no máximo 15 caracteres.'),
  ecg: z.string().trim().max(20, 'ECG deve ter no máximo 20 caracteres.').optional(),
  estado: z.string().min(1, 'Selecione o estado da agenda.'),
});

const defaultValues = {
  pacienteBusca: '',
  paciente: null,
  procedimento: '',
  convenio: '',
  horario: '',
  dataHora: new Date(),
  observacao: '',
  atividade: 'CONSULTA',
  ecg: '',
  estado: 'Aberta',
};

function mapAgendaToFormDefaults(agendaContext) {
  if (!agendaContext) return { ...defaultValues, dataHora: new Date() };

  const nomePaciente = agendaContext.NOME || agendaContext.PACIENTE || '';
  const parsedDataHora = agendaContext.HORAAGENDA_FORMATADA
    ? new Date(agendaContext.HORAAGENDA_FORMATADA)
    : new Date();
  const safeDataHora = Number.isNaN(parsedDataHora.getTime()) ? new Date() : parsedDataHora;

  return {
    pacienteBusca: nomePaciente,
    paciente: agendaContext.CODPAC
      ? {
          CODPAC: agendaContext.CODPAC,
          NOMEPAC: nomePaciente,
          CELULARPAC: agendaContext.CELULARPAC || '',
        }
      : null,
    procedimento: agendaContext.CODPROCEDI ? String(agendaContext.CODPROCEDI) : '',
    convenio: agendaContext.CODCONV ? String(agendaContext.CODCONV) : '',
    horario: agendaContext.HORARIO != null ? String(agendaContext.HORARIO) : '',
    dataHora: safeDataHora,
    observacao: agendaContext.OBSERVACAO || '',
    atividade: agendaContext?.ATIVIDADE || 'CONSULTA',
    ecg: agendaContext?.ECG || '',
    estado: agendaContext?.SITUACAO || 'Aberta',
  };
}

export function getPacienteContato(paciente) {
  return paciente?.CELULARPAC || paciente?.FONERESPAC || paciente?.FONECOMPAC || '';
}

export function dataHoraDoHorario(dataSelecionada, horario) {
  const texto = dataSelecionada instanceof Date ? '' : String(dataSelecionada ?? '');
  const base =
    dataSelecionada instanceof Date
      ? new Date(
          dataSelecionada.getFullYear(),
          dataSelecionada.getMonth(),
          dataSelecionada.getDate()
        )
      : parseISO(texto.slice(0, 10));
  const numero = Number(horario);
  base.setHours(Math.floor(numero / 100), numero % 100, 0, 0);
  return base;
}

export function formatAgendaDateTime(date) {
  const p = (v) => String(v).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}:00`;
}

export function useFormAgenda({
  show,
  agendaContext,
  medicoSelecionado,
  dataSelecionada,
  onAgendaCreated,
  onHide,
}) {
  const func = useSelector(selectFunc);
  const doctor = useSelector(selectDoctor);
  const [buscaPaciente, setBuscaPaciente] = useState('');
  const [buscaPacienteDebounced, setBuscaPacienteDebounced] = useState('');
  const [codPacInput, setCodPacInput] = useState('');
  const [codMedInput, setCodMedInput] = useState('');
  const [showPacienteModal, setShowPacienteModal] = useState(false);
  const [modalBuscaPaciente, setModalBuscaPaciente] = useState('');
  const [apiError, setApiError] = useState(null);
  const convenioInputRef = useRef(null);
  const procedimentoInputRef = useRef(null);
  const observacaoInputRef = useRef(null);
  const horarioInputRef = useRef(null);
  const modalBuscaInputRef = useRef(null);
  const shouldFocusConvenioAfterModalRef = useRef(false);
  const initialValues = useMemo(() => mapAgendaToFormDefaults(agendaContext), [agendaContext]);
  const initialCodMed = agendaContext?.CODMED || medicoSelecionado?.codMed || '';
  const form = useForm({ resolver: zodResolver(agendaSchema), defaultValues });
  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const pacienteSelecionado = watch('paciente');
  const convenioCodigo = watch('convenio');
  const procedimentoCodigo = watch('procedimento');
  const horarioInt = watch('horario');

  // debounce busca paciente
  useEffect(() => {
    const id = window.setTimeout(() => setBuscaPacienteDebounced(buscaPaciente.trim()), 350);
    return () => window.clearTimeout(id);
  }, [buscaPaciente]);

  // reset ao abrir
  useEffect(() => {
    if (!show) return;
    reset(initialValues);
    setBuscaPaciente('');
    setBuscaPacienteDebounced('');
    setModalBuscaPaciente('');
    setCodPacInput(initialValues?.paciente?.CODPAC ? String(initialValues.paciente.CODPAC) : '');
    setCodMedInput(String(initialCodMed));
  }, [show, reset, initialValues, initialCodMed]);

  // foco após fechar modal paciente
  useEffect(() => {
    if (showPacienteModal) {
      window.requestAnimationFrame(() => {
        modalBuscaInputRef.current?.focus();
        modalBuscaInputRef.current?.select?.();
      });
      return;
    }
    if (!shouldFocusConvenioAfterModalRef.current) return;
    const id = window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        convenioInputRef.current?.focus();
        convenioInputRef.current?.select?.();
      });
      shouldFocusConvenioAfterModalRef.current = false;
    }, 120);
    return () => window.clearTimeout(id);
  }, [showPacienteModal]);

  // SWR
  const shouldSearchPaciente = buscaPacienteDebounced.length >= 3;
  const {
    data: pacientes = [],
    isLoading: isLoadingPacientes,
    error: pacientesError,
  } = useSWR(
    shouldSearchPaciente ? ['agenda-pacientes', buscaPacienteDebounced] : null,
    ([, nome]) => buscarPacientesPorNome({ nome }),
    { shouldRetryOnError: false }
  );
  const {
    data: procedimentos = [],
    isLoading: isLoadingProcedimentos,
    error: procedimentosError,
  } = useSWR('agenda-procedimentos', buscarProcedimentosAgenda, {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  });
  const {
    data: convenios = [],
    isLoading: isLoadingConvenios,
    error: conveniosError,
  } = useSWR('agenda-convenios', buscarConveniosAgenda, {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  });

  // handlers
  const focusAndSelect = (ref) =>
    window.requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.select?.();
    });

  const openPacienteModal = () => {
    const query = pacienteSelecionado?.NOMEPAC || '';
    setModalBuscaPaciente(query);
    setBuscaPaciente(query);
    setShowPacienteModal(true);
  };

  const handleSelectPaciente = (paciente) => {
    setValue('paciente', paciente, { shouldValidate: true });
    setValue('pacienteBusca', paciente.NOMEPAC || '', { shouldValidate: true });
    setCodPacInput(String(paciente?.CODPAC || ''));
    setBuscaPaciente(paciente.NOMEPAC || '');
    setBuscaPacienteDebounced(paciente.NOMEPAC || '');
    setModalBuscaPaciente(paciente.NOMEPAC || '');
    shouldFocusConvenioAfterModalRef.current = true;
    setShowPacienteModal(false);
  };

  const handleChangeCodPac = (e) => {
    const next = e.target.value.replace(/\D/g, '');
    setCodPacInput(next);
    if (pacienteSelecionado && String(pacienteSelecionado.CODPAC) !== next) {
      setValue('paciente', null, { shouldValidate: true });
      setValue('pacienteBusca', '', { shouldValidate: true });
    }
  };

  const handleAdvanceOnEnter = (e, nextRef) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    focusAndSelect(nextRef);
  };

  const handleClose = () => {
    reset({ ...defaultValues, dataHora: new Date() });
    setBuscaPaciente('');
    setBuscaPacienteDebounced('');
    setModalBuscaPaciente('');
    setShowPacienteModal(false);
    onHide();
  };

  const onSubmit = async (values) => {
    setApiError(null);

    const codMedParaSalvar = codMedInput || medicoSelecionado?.codMed;
    const isEditing = Boolean(agendaContext?.CODAGENDA);

    if (!codMedParaSalvar) {
      toast.warn('Selecione um médico antes de criar a agenda.');
      return;
    }

    const procedimento = procedimentos.find(
      (p) => String(p.CODPROCEDI) === String(values.procedimento)
    );
    const convenio = convenios.find((c) => String(c.CODCONV) === String(values.convenio));

    if (!procedimento || !convenio) {
      toast.warn('Confira procedimento e convênio carregados do backend antes de salvar.');
      return;
    }

    const { codFunc, nome: nomeUsuario } = resolveUsuarioAgenda({
      func,
      doctor,
    });

    // Define o estado baseado no fluxo:
    // - Na edição: se estava 'Aberta', passa para 'Marcada' (pois foi preenchida/alterada). Mantém o estado atual se já for Confirmada/etc.
    // - Na criação: se houver paciente selecionado, cria como 'Marcada', senão entra como 'Aberta'.
    const temPaciente = Boolean(values.paciente?.CODPAC);
    const estadoDefinido = isEditing
      ? values.estado === 'Aberta'
        ? 'Marcada'
        : values.estado
      : temPaciente
        ? 'Marcada'
        : 'Aberta';

    const nomePaciente = String(values.paciente?.NOMEPAC || '').trim();
    const nomeCabeNaAgenda = nomePaciente.length > 0 && nomePaciente.length <= LIMITE_NOME_AGENDA;

    const payload = {
      codPac: Number(values.paciente?.CODPAC || 1),
      contato: getPacienteContato(values.paciente),
      codProcedi: Number(procedimento.CODPROCEDI),
      atividade: values.atividade?.trim().toUpperCase(),
      ecg: values.ecg?.trim().toUpperCase(),
      codConv: Number(convenio.CODCONV),
      convenio: convenio.SIGLACONV,
      codMed: Number(codMedParaSalvar),
      horario: Number(values.horario),
      dataHora: formatAgendaDateTime(dataHoraDoHorario(dataSelecionada, values.horario)),
      dataAgenda: formatDateISO(
        dataSelecionada instanceof Date ? dataSelecionada : parseISO(String(dataSelecionada))
      ),
      estado: estadoDefinido,
      obs: values.observacao || '',
      codFuncAgenda: codFunc,
      ...(!isEditing && codFunc ? { codFunc, codFuncMarcou: codFunc } : {}),
      evolucao: `${nomeUsuario} - ${isEditing ? 'Editou Agenda' : 'Criou Agenda'}`,
    };

    if (nomeCabeNaAgenda) {
      payload.paciente = nomePaciente;
    } else if (!isEditing) {
      payload.paciente = null;
    }

    try {
      let response;

      if (isEditing) {
        response = await editarAgenda({
          codAgenda: Number(agendaContext.CODAGENDA),
          ...payload,
        });
      } else {
        response = await criarAgenda(payload);
      }

      if (response?.aviso?.aviso) {
        setApiError(response.aviso);
      } else {
        toast.success(response?.message);
      }

      await onAgendaCreated?.();
    } catch (error) {
      setApiError(
        getApiErrorMessage(error, isEditing ? 'Erro ao atualizar agenda.' : 'Erro ao criar agenda.')
      );
    }
  };

  // dados derivados para a UI
  const medicoNome = (() => {
    const found = medicos.find((m) => String(m.codMed) === String(codMedInput || ''));
    return (
      found?.nome || found?.apelido || medicoSelecionado?.nome || medicoSelecionado?.apelido || ''
    );
  })();

  const selectedDataAgenda = agendaContext?.DATAAGENDA
    ? new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeZone: 'America/Sao_Paulo',
      }).format(new Date(new Date(agendaContext.DATAAGENDA).getTime() + 3 * 60 * 60 * 1000))
    : '';

  // campos audit consolidados (elimina as 6 variáveis soltas)
  const audit = {
    marcou: agendaContext?.NOMEFUNCAGENDA || '',
    novaAgenda: agendaContext?.NOMEFUNC || '',
    faltouAtendeu: agendaContext?.NOMEFUNCMARCOU || '',
    atendido: agendaContext?.NOMEFUNCATENDIDO || '',
    celular:
      agendaContext?.CELULARPAC ||
      agendaContext?.TELEFONE ||
      pacienteSelecionado?.CELULARPAC ||
      pacienteSelecionado?.FONERESPAC ||
      '',
  };

  const isLoadingCatalogos = isLoadingProcedimentos || isLoadingConvenios;
  const hasCatalogosError = Boolean(procedimentosError || conveniosError);

  // TODO: rever esse monte de retornos - ficou muito complexo já.
  return {
    form,
    handleSubmit,
    register,
    errors,
    isSubmitting,
    // estado local
    codPacInput,
    codMedInput,
    convenioCodigo,
    procedimentoCodigo,
    horarioInt,
    pacienteSelecionado,
    showPacienteModal,
    setShowPacienteModal,
    modalBuscaPaciente,
    setModalBuscaPaciente,
    modalBuscaInputRef,
    // refs de foco
    convenioInputRef,
    procedimentoInputRef,
    observacaoInputRef,
    horarioInputRef,
    // dados remotos
    pacientes,
    isLoadingPacientes,
    pacientesError,
    shouldSearchPaciente,
    procedimentos,
    isLoadingProcedimentos,
    convenios,
    isLoadingConvenios,
    isLoadingCatalogos,
    hasCatalogosError,
    // dados derivados
    medicoNome,
    selectedDataAgenda,
    audit,
    situacaoAgenda: agendaContext?.SITUACAO || 'Aberta',
    evolucao: agendaContext?.EVOLUCAO || '',
    isEditing: Boolean(agendaContext?.CODAGENDA),
    // handlers
    openPacienteModal,
    handleSelectPaciente,
    handleChangeCodPac,
    handleChangeCodConvenio: (e) =>
      setValue('convenio', e.target.value.replace(/\D/g, ''), {
        shouldValidate: true,
      }),
    handleChangeCodProcedimento: (e) =>
      setValue('procedimento', e.target.value.replace(/\D/g, ''), {
        shouldValidate: true,
      }),
    handleChangeHorario: (e) =>
      setValue('horario', e.target.value.replace(/\D/g, '').slice(0, 4), {
        shouldValidate: true,
      }),
    handleAdvanceOnEnter,
    handleHorarioKeyDown: (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    },
    handleAtividadeKeyDown: (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    },
    handleClose,
    onSubmit,
    setBuscaPaciente,
    apiError,
    setApiError,
  };
}

export const useAgendaForm = useFormAgenda;
export default useFormAgenda;
